/**
 * AI Agency — sección completa con pipeline de lead-gen, landing builder, outreach,
 * clientes cerrados y constructor de sistemas IA personalizados (Senia Stands type).
 *
 * Tabs:
 *  - Dashboard (KPIs + funnel)
 *  - Leads (Google Maps scanner + manual)
 *  - Landings (generadas con IA)
 *  - Outreach (mensajes/llamadas registrados)
 *  - Clientes (cerrados + MRR)
 *  - Automations (sistemas IA por cliente)
 */
import { h, apiClient, formatEur, btn, toast, emptyState, skeletonCard, skeletonGrid, openModal, pill } from './_common.js';

let _agTab = 'dashboard';

export async function renderAgency(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'max-w-7xl mx-auto px-6 py-6 space-y-6 animate-fade-in' });
  root.appendChild(wrap);

  // Header
  wrap.appendChild(h('div', { class: 'flex items-center justify-between' }, [
    h('div', {}, [
      h('h2', { class: 'font-display text-3xl font-bold' }, '💼 AI Agency'),
      h('p', { class: 'text-sm text-ink-300' }, 'Pipeline lead-gen → landing AI → outreach → cierre → sistema IA personalizado.'),
    ]),
    h('div', { class: 'flex gap-2' }, [
      btn('+ Lead manual', () => openLeadModal(root), { variant: 'secondary' }),
      btn('🔍 Scan Google Maps', () => openScanModal(root)),
    ]),
  ]));

  // Quick links — herramientas Agency
  try {
    const { renderQuickLinks } = await import('./_quick-links.js');
    const qlContainer = h('div');
    wrap.appendChild(qlContainer);
    await renderQuickLinks(qlContainer, 'agency', {
      title: '🔗 Mis herramientas Agency',
      subtitle: 'Google Maps, CRM, email outreach, Calendly, Stripe, etc.',
      onReload: () => renderAgency(root),
    });
  } catch { /* skip */ }

  // Tabs
  const tabs = [
    { id: 'dashboard',  label: '📊 Dashboard' },
    { id: 'leads',      label: '🎯 Leads' },
    { id: 'landings',   label: '🚀 Landings' },
    { id: 'outreach',   label: '✉️ Outreach' },
    { id: 'clientes',   label: '💰 Clientes' },
    { id: 'automations',label: '🤖 Automations' },
  ];
  const tabBar = h('div', { class: 'flex gap-1 border-b border-base-700 overflow-x-auto' });
  tabs.forEach((t) => {
    const isActive = t.id === _agTab;
    tabBar.appendChild(h('button', {
      class: `px-3 py-2 text-sm font-medium border-b-2 transition whitespace-nowrap ${isActive ? 'border-gold-400 text-gold-300' : 'border-transparent text-ink-300 hover:text-ink-100'}`,
      onclick: () => { _agTab = t.id; renderAgency(root); },
    }, t.label));
  });
  wrap.appendChild(tabBar);

  const content = h('div', {});
  wrap.appendChild(content);

  if (_agTab === 'dashboard')   await renderDashboard(content, root);
  if (_agTab === 'leads')       await renderLeads(content, root);
  if (_agTab === 'landings')    await renderLandings(content, root);
  if (_agTab === 'outreach')    await renderOutreach(content, root);
  if (_agTab === 'clientes')    await renderClientes(content, root);
  if (_agTab === 'automations') await renderAutomations(content, root);
}

// =================== DASHBOARD ===================
async function renderDashboard(content, root) {
  content.appendChild(skeletonGrid(4, 4));
  try {
    const s = await apiClient.get('/api/v1/agency/stats');
    content.innerHTML = '';
    const k = s.stats || s;

    content.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3' }, [
      kpi('Leads totales', String(k.totalLeads ?? 0), 'text-ink-100', 'capturados'),
      kpi('Sin web (oportunidad)', String(k.leadsNoWebsite ?? 0), 'text-emerald-300', 'lead-gen prioritario'),
      kpi('Clientes activos', String(k.activeClients ?? 0), 'text-gold-300', 'cerrados'),
      kpi('MRR estimado', formatEur(k.mrr ?? 0), 'text-gold-300', `${k.totalRevenue ? '€' + (k.totalRevenue).toFixed(0) + ' lifetime' : ''}`),
    ]));

    // Funnel
    content.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-5 mt-6' }, [
      h('h3', { class: 'font-display font-semibold mb-4' }, '🚀 Funnel del pipeline'),
      h('div', { class: 'grid grid-cols-2 md:grid-cols-5 gap-2' }, [
        funnelStep('Nuevos', k.byStatus?.new ?? 0, 'bg-base-700'),
        funnelStep('Contactados', k.byStatus?.contacted ?? 0, 'bg-sky-400/20'),
        funnelStep('Interesados', k.byStatus?.interested ?? 0, 'bg-emerald-400/20'),
        funnelStep('Demos', k.byStatus?.demo ?? 0, 'bg-gold-400/30'),
        funnelStep('Cerrados', k.byStatus?.closed ?? 0, 'bg-gold-500/40'),
      ]),
    ]));

    if (!k.googleMapsConfigured) {
      content.appendChild(h('div', { class: 'mt-6 bg-amber-500/10 border border-amber-400/40 rounded-xl p-4' }, [
        h('div', { class: 'flex items-start gap-3' }, [
          h('div', { class: 'text-2xl' }, '⚠️'),
          h('div', {}, [
            h('div', { class: 'font-semibold text-amber-300' }, 'Google Maps API no configurada'),
            h('p', { class: 'text-sm text-ink-300 mt-1' }, 'Para escanear automáticamente negocios sin web en Google Maps, añade tu API key en Ajustes → Integraciones → Google Maps.'),
            h('a', { href: 'https://console.cloud.google.com/apis/library/places-backend.googleapis.com', target: '_blank', rel: 'noopener', class: 'text-amber-300 text-sm underline mt-2 inline-block' }, '→ Obtener API key Google Maps Places'),
          ]),
        ]),
      ]));
    }
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(emptyState('💼', 'Sin datos todavía', 'Empieza añadiendo leads o escaneando Google Maps.', btn('Scan Google Maps', () => openScanModal(root))));
  }
}

// =================== LEADS ===================
async function renderLeads(content, root) {
  content.appendChild(skeletonCard(4));
  try {
    const r = await apiClient.get('/api/v1/agency/leads');
    content.innerHTML = '';
    if (!r.leads || r.leads.length === 0) {
      content.appendChild(emptyState('🎯', 'Sin leads aún',
        'Escanea Google Maps o añade leads manualmente para empezar el funnel.',
        h('div', { class: 'flex gap-2 justify-center mt-2' }, [
          btn('+ Manual', () => openLeadModal(root), { variant: 'secondary' }),
          btn('🔍 Scan Maps', () => openScanModal(root)),
        ])));
      return;
    }

    // Filtros
    const filters = h('div', { class: 'flex gap-2 mb-3' });
    const statuses = ['todos', 'new', 'contacted', 'interested', 'demo', 'closed'];
    statuses.forEach((s) => filters.appendChild(h('button', {
      class: 'text-xs px-3 py-1 rounded-full bg-base-700 hover:bg-base-600 text-ink-200',
      onclick: () => {
        const filtered = s === 'todos' ? r.leads : r.leads.filter((l) => l.status === s);
        list.innerHTML = '';
        filtered.forEach((l) => list.appendChild(buildLeadCard(l, root)));
      },
    }, s)));
    content.appendChild(filters);

    const list = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 gap-3' });
    r.leads.forEach((l) => list.appendChild(buildLeadCard(l, root)));
    content.appendChild(list);
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(emptyState('⚠️', 'Error cargando leads', e?.message || 'Reintenta', null));
  }
}

function buildLeadCard(l, root) {
  const statusColor = { new: 'gray', contacted: 'blue', interested: 'green', demo: 'gold', closed: 'gold' }[l.status] || 'gray';
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4 hover:border-gold-400/30 transition' }, [
    h('div', { class: 'flex items-start justify-between mb-2' }, [
      h('div', {}, [
        h('div', { class: 'font-semibold' }, l.businessName),
        h('div', { class: 'text-xs text-ink-300' }, [
          l.category, l.city, l.googleRating ? `★ ${l.googleRating}` : null,
        ].filter(Boolean).join(' · ')),
      ]),
      pill(l.status || 'new', statusColor),
    ]),
    h('div', { class: 'text-xs text-ink-300 space-y-0.5' }, [
      l.phone ? h('div', {}, '📞 ' + l.phone) : null,
      l.email ? h('div', {}, '✉️ ' + l.email) : null,
      l.website ? h('div', {}, '🌐 ' + l.website) : h('div', { class: 'text-emerald-300' }, '✓ Sin web — oportunidad'),
      (l.source === 'autosell_web' && l.notes && l.notes.indexOf('Atribución:') >= 0) ? h('div', { class: 'text-sky-300 mt-1', title: l.notes }, '🎯 ' + (l.notes.split('Atribución:')[1] || '').trim().split('\n')[0].slice(0, 90)) : null,
    ]),
    h('div', { class: 'flex gap-2 mt-3' }, [
      btn('Landing AI', async () => {
        toast('Generando landing con IA...', 'info');
        try {
          await apiClient.post(`/api/v1/agency/leads/${l.id}/landing`, {});
          toast('Landing generada ✓', 'success');
        } catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
      }, { variant: 'secondary' }),
      btn('Outreach', () => openOutreachModal(l, root), { variant: 'secondary' }),
      btn('→ Status', () => openStatusModal(l, root), { variant: 'secondary' }),
    ]),
  ]);
}

// =================== LANDINGS ===================
async function renderLandings(content, root) {
  content.appendChild(skeletonCard(3));
  try {
    const r = await apiClient.get('/api/v1/agency/leads');
    const leadsWithLanding = (r.leads || []).filter((l) => l.landingUrl || l.landingHtml);
    content.innerHTML = '';
    if (leadsWithLanding.length === 0) {
      content.appendChild(emptyState('🚀', 'Sin landings aún',
        'Genera una landing con IA desde la pestaña Leads (botón "Landing AI").', null));
      return;
    }
    const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' });
    leadsWithLanding.forEach((l) => grid.appendChild(
      h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
        h('div', { class: 'font-semibold text-sm' }, l.businessName),
        h('div', { class: 'text-xs text-ink-300 mb-2' }, l.category || ''),
        l.landingUrl ? h('a', { href: l.landingUrl, target: '_blank', rel: 'noopener', class: 'text-gold-300 text-xs underline' }, '🔗 Ver landing') : null,
        h('div', { class: 'mt-3 text-xs text-ink-300' }, l.landingGeneratedAt ? `Generada ${new Date(l.landingGeneratedAt).toLocaleDateString()}` : ''),
      ]),
    ));
    content.appendChild(grid);
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(emptyState('⚠️', 'Error', e?.message || 'Reintenta', null));
  }
}

// =================== OUTREACH ===================
async function renderOutreach(content, root) {
  content.appendChild(skeletonCard(3));
  try {
    const r = await apiClient.get('/api/v1/agency/leads');
    const leads = r.leads || [];
    content.innerHTML = '';
    if (leads.length === 0) {
      content.appendChild(emptyState('✉️', 'Sin outreach todavía', 'Captura leads primero.', null));
      return;
    }
    content.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('h3', { class: 'font-display font-semibold mb-3' }, '📋 Histórico de outreach'),
      h('div', { class: 'space-y-2 max-h-[60vh] overflow-y-auto' },
        leads.filter((l) => l.lastContactedAt).map((l) => h('div', { class: 'flex items-center justify-between py-2 border-b border-base-700 last:border-0' }, [
          h('div', {}, [
            h('div', { class: 'font-medium text-sm' }, l.businessName),
            h('div', { class: 'text-xs text-ink-300' }, `Último contacto: ${new Date(l.lastContactedAt).toLocaleString()}`),
          ]),
          pill(l.status, 'gold'),
        ]))),
    ]));
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(emptyState('⚠️', 'Error', e?.message || 'Reintenta', null));
  }
}

// =================== CLIENTES ===================
async function renderClientes(content, root) {
  content.appendChild(skeletonCard(3));
  try {
    const r = await apiClient.get('/api/v1/agency/clients');
    content.innerHTML = '';
    if (!r.clients || r.clients.length === 0) {
      content.appendChild(emptyState('💰', 'Sin clientes cerrados aún',
        'Cuando cierres un lead, conviértelo en cliente desde el lead card.', null));
      return;
    }
    const total = r.clients.reduce((s, c) => s + (c.monthlyRecurringRevenue || 0), 0);
    content.appendChild(h('div', { class: 'bg-gold-400/10 border border-gold-400/40 rounded-xl p-4 mb-4' }, [
      h('div', { class: 'text-xs uppercase tracking-wider text-gold-300' }, 'MRR Total'),
      h('div', { class: 'text-3xl font-display font-bold text-gold-300' }, formatEur(total)),
    ]));
    const list = h('div', { class: 'space-y-2' });
    r.clients.forEach((c) => list.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4 flex items-center justify-between' }, [
      h('div', {}, [
        h('div', { class: 'font-semibold' }, c.businessName),
        h('div', { class: 'text-xs text-ink-300' }, `${c.serviceType || 'Sistema IA'} · cerrado ${new Date(c.closedAt).toLocaleDateString()}`),
      ]),
      h('div', { class: 'text-right' }, [
        h('div', { class: 'text-gold-300 font-semibold' }, formatEur(c.monthlyRecurringRevenue || 0) + '/mes'),
        h('div', { class: 'text-xs text-ink-300' }, c.totalRevenue ? 'Lifetime: ' + formatEur(c.totalRevenue) : ''),
      ]),
    ])));
    content.appendChild(list);
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(emptyState('⚠️', 'Error', e?.message || 'Reintenta', null));
  }
}

// =================== AUTOMATIONS ===================
async function renderAutomations(content, root) {
  content.appendChild(skeletonCard(3));
  try {
    const r = await apiClient.get('/api/v1/agency/automations');
    content.innerHTML = '';
    if (!r.automations || r.automations.length === 0) {
      content.appendChild(emptyState('🤖', 'Sin sistemas IA construidos aún',
        'Construye un sistema IA personalizado para un cliente (chatbot, automatización, agente Senia Stands type).',
        btn('+ Nuevo sistema', () => openAutomationModal(root))));
      return;
    }
    const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 gap-3' });
    r.automations.forEach((a) => grid.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('div', { class: 'flex items-start justify-between mb-2' }, [
        h('div', { class: 'font-semibold' }, a.name),
        pill(a.status || 'active', a.status === 'active' ? 'green' : 'gray'),
      ]),
      h('div', { class: 'text-xs text-ink-300' }, a.description || ''),
      h('div', { class: 'text-xs mt-2 text-gold-300' }, `Cliente: ${a.clientName || 'N/A'} · ${formatEur(a.monthlyValue || 0)}/mes`),
    ])));
    content.appendChild(grid);
    content.appendChild(h('div', { class: 'mt-4 text-right' }, btn('+ Nuevo sistema', () => openAutomationModal(root))));
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(emptyState('⚠️', 'Error', e?.message || 'Reintenta', null));
  }
}

// =================== HELPERS ===================
function kpi(label, value, color = 'text-ink-100', sub = '') {
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-5' }, [
    h('div', { class: 'text-xs uppercase tracking-wider text-ink-300 mb-1' }, label),
    h('div', { class: `text-2xl font-display font-bold ${color}` }, value),
    sub ? h('div', { class: 'text-xs text-ink-300 mt-1' }, sub) : null,
  ]);
}

function funnelStep(label, count, bgClass) {
  return h('div', { class: `${bgClass} rounded-lg p-3 text-center` }, [
    h('div', { class: 'text-xs text-ink-300 mb-1' }, label),
    h('div', { class: 'text-2xl font-bold font-display' }, String(count)),
  ]);
}

// =================== MODALS ===================
function openLeadModal(root) {
  openModal({
    title: 'Añadir lead manual',
    fields: [
      { name: 'businessName', label: 'Nombre del negocio', required: true },
      { name: 'category', label: 'Categoría (ej: restaurante)' },
      { name: 'city', label: 'Ciudad' },
      { name: 'phone', label: 'Teléfono' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'website', label: 'Web (deja vacío si no tiene)' },
      { name: 'googleRating', label: 'Rating Google (1-5)', type: 'number', step: '0.1' },
    ],
    submitLabel: 'Añadir lead',
    onSubmit: async (data) => {
      const body = { ...data };
      if (body.googleRating) body.googleRating = Number(body.googleRating);
      await apiClient.post('/api/v1/agency/leads', body);
      toast('Lead añadido', 'success');
      renderAgency(root);
    },
  });
}

function openScanModal(root) {
  openModal({
    title: 'Scan Google Maps',
    subtitle: 'Encuentra negocios sin web en tu zona — leads de oro.',
    fields: [
      { name: 'city', label: 'Ciudad', required: true, value: 'Alicante' },
      { name: 'category', label: 'Categoría (ej: restaurante, fontanero, peluquería)', required: true },
      { name: 'radius', label: 'Radio km', type: 'number', value: '5' },
      { name: 'maxResults', label: 'Max resultados', type: 'number', value: '50' },
    ],
    submitLabel: 'Escanear',
    onSubmit: async (data) => {
      toast('Escaneando Google Maps...', 'info', 60000);
      try {
        const r = await apiClient.post('/api/v1/agency/leads/scan-maps', {
          query: ((data.category || '').trim() + ' ' + (data.city || '').trim()).trim(),
          location: data.city,
          maxResults: Math.max(5, Math.min(Number(data.maxResults) || 20, 60)),
        });
        toast(`✓ ${r.added || 0} leads nuevos añadidos (${r.skipped || 0} duplicados, ${r.withWebsite || 0} con web)`, 'success');
        renderAgency(root);
      } catch (e) {
        toast('Error escaneando Maps: ' + (e?.message || e?.error || 'desconocido'), 'error');
      }
    },
  });
}

function openOutreachModal(lead, root) {
  openModal({
    title: `Outreach → ${lead.businessName}`,
    fields: [
      { name: 'channel', label: 'Canal', type: 'select', options: ['email', 'whatsapp', 'phone', 'linkedin'], value: 'email' },
      { name: 'message', label: 'Mensaje / nota', type: 'textarea', required: true },
    ],
    submitLabel: 'Registrar contacto',
    onSubmit: async (data) => {
      await apiClient.post(`/api/v1/agency/leads/${lead.id}/outreach`, data);
      toast('Outreach registrado', 'success');
      renderAgency(root);
    },
  });
}

function openStatusModal(lead, root) {
  openModal({
    title: `Actualizar estado → ${lead.businessName}`,
    fields: [
      { name: 'status', label: 'Estado', type: 'select', options: ['new', 'contacted', 'interested', 'demo', 'closed', 'lost'], value: lead.status || 'new' },
      { name: 'notes', label: 'Notas', type: 'textarea' },
    ],
    submitLabel: 'Actualizar',
    onSubmit: async (data) => {
      await apiClient.patch(`/api/v1/agency/leads/${lead.id}`, data);
      toast('Estado actualizado', 'success');
      renderAgency(root);
    },
  });
}

function openAutomationModal(root) {
  openModal({
    title: 'Nuevo sistema IA personalizado',
    subtitle: 'Define el sistema que vas a construir para tu cliente.',
    fields: [
      { name: 'name', label: 'Nombre del sistema', required: true },
      { name: 'clientName', label: 'Cliente (negocio)', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      { name: 'monthlyValue', label: 'Valor mensual (€)', type: 'number' },
    ],
    submitLabel: 'Crear sistema',
    onSubmit: async (data) => {
      const body = { ...data };
      if (body.monthlyValue) body.monthlyValue = Number(body.monthlyValue);
      await apiClient.post('/api/v1/agency/automations', body);
      toast('Sistema creado', 'success');
      renderAgency(root);
    },
  });
}
